import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { ShieldCheck, Download, Eye, RefreshCw, CalendarClock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function download(path, filename) {
  const token = localStorage.getItem("token");
  return fetch(`${API}/api${path}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.blob())
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    });
}

function CountChips({ counts }) {
  const entries = Object.entries(counts || {});
  if (!entries.length) return <span className="text-[#9A8F7E] text-sm">No identifiers detected in free text.</span>;
  return (
    <div className="flex flex-wrap gap-2" data-testid="deid-redaction-counts">
      {entries.map(([k, v]) => (
        <span key={k} className="text-[11px] uppercase tracking-widest bg-[#EDE7DC] text-[#5C6359] rounded-full px-2.5 py-1">
          {k.replace(/_/g, " ")} × {v}
        </span>
      ))}
    </div>
  );
}

export default function AdminResearch() {
  const [members, setMembers] = useState(null);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadMembers = useCallback(async () => {
    try {
      const res = await api.get("/admin/deid/members");
      setMembers(res.data);
    } catch (_) { setMembers([]); }
  }, []);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const runPreview = async (uid) => {
    setSelected(uid);
    setPreview(null);
    setLoading(true);
    try {
      const res = await api.get(`/admin/deid/preview/${uid}`);
      setPreview(res.data);
    } catch (_) { setPreview({ error: true }); } finally {
      setLoading(false);
    }
  };

  const doExport = async (fmt, uid) => {
    setExporting(true);
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const q = uid ? `&user_id=${uid}` : "";
    try {
      await download(`/admin/deid/export?fmt=${fmt}${q}`, `badi-deid-${uid || "cohort"}-${stamp}.${fmt}`);
    } finally { setExporting(false); }
  };

  return (
    <AdminLayout>
      <p className="label-eyebrow mb-2">Admin · Research</p>
      <h1 className="font-serif text-4xl text-[#2D322B] leading-tight mb-2">De-identify for research.</h1>
      <p className="text-[#5C6359] mb-4 text-sm max-w-3xl flex items-start gap-2">
        <ShieldCheck className="w-4 h-4 text-[#3F6B45] shrink-0 mt-0.5" />
        <span>
          Names become stable pseudonyms and free-text notes are scrubbed of identifiers (Presidio + spaCy).
          Lab dates are shifted by a fixed per-patient offset — <strong className="font-medium">the gaps between draws are preserved</strong>,
          so trends stay analysable while real dates are removed.
        </span>
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8">
        {/* Member list */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase tracking-widest text-[#9A8F7E]">Members with data</p>
            <button onClick={loadMembers} data-testid="deid-refresh" className="text-[#9A8F7E] hover:text-[#2D322B]">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="space-y-2" data-testid="deid-member-list">
            {members === null && <p className="text-sm text-[#9A8F7E]">Loading…</p>}
            {members?.length === 0 && <p className="text-sm text-[#9A8F7E]">No members have lab or microbiome data yet.</p>}
            {members?.map((m) => (
              <button
                key={m.user_id}
                onClick={() => runPreview(m.user_id)}
                data-testid={`deid-member-${m.user_id}`}
                className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors ${selected === m.user_id ? "border-[#2D322B] bg-white" : "border-[#E8DFD0] hover:bg-white"}`}
              >
                <p className="text-sm text-[#2D322B] truncate">{m.name}</p>
                <p className="text-xs text-[#9A8F7E] truncate">{m.email}</p>
                <p className="text-[11px] text-[#5C6359] mt-1">{m.biomarker_count} labs · {m.microbiome_count} gut samples</p>
              </button>
            ))}
          </div>

          <div className="mt-6 border-t border-[#E8DFD0] pt-4">
            <p className="text-xs uppercase tracking-widest text-[#9A8F7E] mb-3">Export whole cohort</p>
            <div className="flex gap-2">
              <button onClick={() => doExport("json")} disabled={exporting} data-testid="deid-export-cohort-json"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#3F6B45] border border-[#BBD0B7] rounded-full px-3 py-2 disabled:opacity-50">
                <Download className="w-3.5 h-3.5" /> JSON
              </button>
              <button onClick={() => doExport("csv")} disabled={exporting} data-testid="deid-export-cohort-csv"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#3F6B45] border border-[#BBD0B7] rounded-full px-3 py-2 disabled:opacity-50">
                <Download className="w-3.5 h-3.5" /> CSV
              </button>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div data-testid="deid-preview">
          {!selected && (
            <div className="border border-dashed border-[#E8DFD0] rounded-xl p-12 text-center text-[#9A8F7E]">
              <Eye className="w-6 h-6 mx-auto mb-3" strokeWidth={1.5} />
              Select a member to preview their de-identified record.
            </div>
          )}
          {loading && (
            <div className="border border-[#E8DFD0] rounded-xl p-12 text-center text-[#5C6359]">
              <RefreshCw className="w-5 h-5 mx-auto mb-3 animate-spin" /> De-identifying… (first run loads the NLP model)
            </div>
          )}
          {preview && !loading && !preview.error && (
            <div className="space-y-6">
              <div className="bg-white border border-[#E8DFD0] rounded-xl p-5">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div>
                    <p className="label-eyebrow">Pseudonym</p>
                    <p className="font-mono text-lg text-[#2D322B]" data-testid="deid-pseudonym">{preview.pseudonym}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => doExport("json", selected)} disabled={exporting} data-testid="deid-export-member-json"
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#3F6B45] border border-[#BBD0B7] rounded-full px-3 py-2 disabled:opacity-50">
                      <Download className="w-3.5 h-3.5" /> JSON
                    </button>
                    <button onClick={() => doExport("csv", selected)} disabled={exporting} data-testid="deid-export-member-csv"
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#3F6B45] border border-[#BBD0B7] rounded-full px-3 py-2 disabled:opacity-50">
                      <Download className="w-3.5 h-3.5" /> CSV
                    </button>
                  </div>
                </div>
                <p className="text-xs text-[#5C6359] flex items-center gap-2 mb-3">
                  <CalendarClock className="w-3.5 h-3.5 text-[#3F6B45]" /> Dates shifted (intervals preserved). Age aggregated to 90+ over 89.
                </p>
                <CountChips counts={preview.redaction_counts} />
              </div>

              {preview.intake && (
                <div className="bg-white border border-[#E8DFD0] rounded-xl p-5">
                  <p className="label-eyebrow mb-3">Intake (de-identified)</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm text-[#2D322B]">
                    <div><span className="text-[#9A8F7E] text-xs block">Age</span>{String(preview.intake.age ?? "—")}</div>
                    <div><span className="text-[#9A8F7E] text-xs block">Sex</span>{preview.intake.sex || "—"}</div>
                    <div><span className="text-[#9A8F7E] text-xs block">Ancestry</span>{preview.intake.ancestry || "—"}</div>
                  </div>
                  {preview.intake.notes && <p className="text-sm text-[#5C6359] mt-3 italic">{preview.intake.notes}</p>}
                </div>
              )}

              <div className="bg-white border border-[#E8DFD0] rounded-xl p-5">
                <p className="label-eyebrow mb-3">Biomarkers · {preview.record_counts.biomarkers}</p>
                <div className="overflow-x-auto max-h-80 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-[#9A8F7E] uppercase tracking-widest text-[11px]">
                      <tr><th className="py-2 pr-4">Marker</th><th className="py-2 pr-4">Value</th><th className="py-2 pr-4">Unit</th><th className="py-2 pr-4">Shifted date</th><th className="py-2">Notes</th></tr>
                    </thead>
                    <tbody>
                      {preview.biomarkers.map((b, i) => (
                        <tr key={i} className="border-t border-[#F1ECE4]">
                          <td className="py-2 pr-4 text-[#2D322B]">{b.marker}</td>
                          <td className="py-2 pr-4 text-[#5C6359]">{b.value}</td>
                          <td className="py-2 pr-4 text-[#9A8F7E]">{b.unit}</td>
                          <td className="py-2 pr-4 font-mono text-[12px] text-[#5C6359]">{b.date}</td>
                          <td className="py-2 text-[#9A8F7E]">{b.notes || "—"}</td>
                        </tr>
                      ))}
                      {preview.biomarkers.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-[#9A8F7E]">No biomarkers.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {preview.microbiome_samples.length > 0 && (
                <div className="bg-white border border-[#E8DFD0] rounded-xl p-5">
                  <p className="label-eyebrow mb-3">Microbiome samples · {preview.record_counts.microbiome_samples}</p>
                  {preview.microbiome_samples.map((s, i) => (
                    <div key={i} className="text-sm text-[#5C6359] border-t border-[#F1ECE4] py-2">
                      <span className="font-mono text-[12px] text-[#9A8F7E]">{s.date}</span> — {s.organisms.length} organisms
                      {s.diversity_note && <span className="italic"> · {s.diversity_note}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {preview?.error && <p className="text-[#8F3A3A] text-sm">Failed to de-identify this member.</p>}
        </div>
      </div>
    </AdminLayout>
  );
}
